import React from 'react';
import {
  ConnectionProfile,
  FUB_FIELDS_MAP_UPDATED, FubFieldMapEntry,
  ProjectType,
  useFubFieldsMappingService
} from 'nekst-api';
import {
  Cell,
  FilterContext,
  Header4, If,
  List,
  SimpleBlock,
  Text,
  useShowContextRequired
} from 'ui-builder';
import { GridLayout, OpenFormIconButton } from 'features/nekst-widgets';
import UpdateFieldMappingForm from './UpdateFieldMappingForm';
import useProjectDetailsFieldTypeHelper
  from '../../../projectdetails/projectDetailsFieldTypeHelper';
import { FubFieldMappingListItem } from './types';
import { SetupAreaFilter } from '../../../../projects/list/SetupAreaFilter';

function titleText(text: string) {
  return text.charAt(0)
    .toUpperCase() + text.slice(1);
}

function FubFieldLabelAndTypeField() {
  const showContext = useShowContextRequired<FubFieldMapEntry>();

  return (
    <Text>
      {showContext.data.fubFieldLabel} ({titleText(showContext.data.fubFieldType)})
    </Text>
  );
}

function NekstFieldLabelAndTypeField() {
  const showContext = useShowContextRequired<FubFieldMappingListItem>();
  const data = showContext.data;

  const detailsFieldHelper = useProjectDetailsFieldTypeHelper();

  const showMarketName = data.mappings.length > 1;

  return data.mappings.map((mappingEntry) => {
    if (mappingEntry.nekstFieldLabel && mappingEntry.nekstFieldType) {
      return (
        <SimpleBlock>
          {showMarketName && (<Text bold>{mappingEntry.setupArea.name}: </Text>)}
          {' '}
          {mappingEntry.nekstFieldLabel} ({detailsFieldHelper.getLabel(mappingEntry.nekstFieldType)})
        </SimpleBlock>
      );
    } else {
      return (
        <SimpleBlock>
          {showMarketName && (<Text bold>{mappingEntry.setupArea.name}: </Text>)}
          {` -`}
        </SimpleBlock>
      );
    }
  });
}

interface FubFieldsFilter {
  setupAreaId?: number;
}

export default function FieldsMappingList(
  props: {
    profile: ConnectionProfile,
    projectType: ProjectType,
  }
) {
  const fubFieldsMappingService = useFubFieldsMappingService();

  const getAllEntries = async (filter: FubFieldsFilter) => {
    let result = await fubFieldsMappingService.getAllEntries(
      props.profile.id,
      props.projectType
    );

    if (filter?.setupAreaId) {
      result = result.filter((entry) => {
        return entry._setupArea.id === filter.setupAreaId;
      });
    }
    const entriesMap = result
      .reduce((previousValue, currentValue) => {
        const key = `${currentValue.fubFieldId}`;
        if (!previousValue[key]) {
          previousValue[key] = [
            currentValue
          ];
        } else {
          // If the entry already exists, we can merge the nekst field data
          previousValue[key].push(currentValue);
        }
        return previousValue;
      }, {} as Record<string, FubFieldMapEntry[]>);

    return Object.values(entriesMap)
      .map((entries) => {
        return {
          fubFieldId: entries[0].fubFieldId,
          fubFieldAlias: entries[0].fubFieldAlias,
          fubFieldLabel: entries[0].fubFieldLabel,
          fubFieldType: entries[0].fubFieldType,
          mappings: entries.map((entry) => ({
            nekstFieldId: entry.nekstFieldId,
            nekstFieldLabel: entry.nekstFieldLabel,
            nekstFieldType: entry.nekstFieldType,
            setupArea: entry._setupArea
          }))
        };
      }) as FubFieldMappingListItem[];
  };

  return (
    <FilterContext initialValue={{
      setupAreaId: null
    }}>
      <Header4>Fields Mapping</Header4>
      <SetupAreaFilter />
      <List
        getDataFunc={getAllEntries}
        dependencies={[props.profile.id, props.projectType]}
        updateOnEvents={[FUB_FIELDS_MAP_UPDATED]}
        loadDataOnFilterUpdate
      >
        <GridLayout>
          <Cell label="Follow Up Boss Field">
            <FubFieldLabelAndTypeField />
          </Cell>
          <Cell label="Nekst Field">
            <NekstFieldLabelAndTypeField />
          </Cell>
          <Cell label="Actions">
            <If conditionFunc={(data) => data.fubFieldId !== 'name'}>
              <OpenFormIconButton form={(
                <UpdateFieldMappingForm
                  projectType={props.projectType}
                  profile={props.profile}
                />
              )}
              />
            </If>
          </Cell>
        </GridLayout>
      </List>
    </FilterContext>
  );
}
